import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import moment, { Moment } from 'moment';
import { format } from 'date-fns';
import { cs, sk, es, enGB, hu, uk, ru, de, fr, pl } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './DatePicker.module.scss';
import { useAppContext } from '../../../../../../Services/AppContext';
import useTranslation from '../../../../../../Hooks/useTranslation';

interface Props {
	value?: Moment | string | null;
	onChange?: (date: Moment | null) => void;
	disabled?: boolean;
	minDate?: Moment;
	maxDate?: Moment;
	placeholderText?: string;
}

const ll: Record<string, any> = {
	cs,
	sk,
	hu,
	en: enGB,
	ru,
	uk,
	es,
	de,
	fr,
	pl,
};

for (const l in ll) {
	registerLocale(l, ll[l]);
}

export const toMomentOrNull = (
	value: Moment | string | null | undefined,
	language?: string,
): Moment | null => {
	if (!value) return null;

	if (moment.isMoment(value)) {
		const m = value.clone();
		return language ? m.locale(language) : m;
	}

	if (typeof value === 'string') {
		const s = value.trim();
		if (!s) return null;

		const m = language ? moment(s).locale(language) : moment(s);

		return m.isValid() ? m : null;
	}

	return null;
};

const DatePicker: React.FC<Props> = ({
	value = null,
	onChange,
	disabled,
	minDate,
	maxDate,
	placeholderText = 'base.pickDate',
}) => {
	const { language } = useAppContext();
	const { t } = useTranslation();
	const wrapperRef = useRef<HTMLDivElement | null>(null);

	const normalizedValue = useMemo(
		() => toMomentOrNull(value, language)?.startOf('day') ?? null,
		[value, language],
	);

	const [selectedDate, setSelectedDate] = useState<Date | null>(
		normalizedValue ? normalizedValue.toDate() : null,
	);
	const [isOpen, setIsOpen] = useState(false);

	useEffect(() => {
		setSelectedDate(normalizedValue ? normalizedValue.toDate() : null);
	}, [normalizedValue]);

	useEffect(() => {
		if (language) moment.locale(language);
	}, [language]);

	const localeObj = useMemo(() => ll[language] ?? ll.cs, [language]);

	const handleChange = (date: Date | null) => {
		setSelectedDate(date);
		onChange?.(date ? moment(date).locale(language).startOf('day') : null);
		setIsOpen(false);
	};

	const setPortalWidth = () => {
		const w = wrapperRef.current?.offsetWidth;
		const portal = document.getElementById('react-datepicker-portal');

		if (w && portal) {
			portal.style.setProperty('--birthdp-width', `${w}px`);
		}
	};

	return (
		<div className='react-birth-date-picker' ref={wrapperRef}>
			<ReactDatePicker
				selected={selectedDate}
				onChange={handleChange}
				open={isOpen}
				onInputClick={() => {
					setPortalWidth();
					setIsOpen(true);
				}}
				onClickOutside={() => setIsOpen(false)}
				locale={language}
				dateFormat='dd.MM.yyyy'
				disabled={disabled}
				minDate={minDate ? minDate.toDate() : undefined}
				maxDate={maxDate ? maxDate.toDate() : undefined}
				placeholderText={t(placeholderText)}
				className={styles.input}
				wrapperClassName='react-birth-date-picker'
				portalId='react-datepicker-portal'
				popperClassName='birthdp-popper'
				calendarClassName='birthdp-calendar'
				renderCustomHeader={({ date, decreaseMonth, increaseMonth }) => {
					const title = format(date, 'LLLL yyyy', { locale: localeObj });

					return (
						<div className='react-datepicker__custom-header'>
							<div className='react-datepicker__custom-header__title'>
								{title}
							</div>
							<div
								className='react-datepicker__custom-header__prev'
								onClick={decreaseMonth}
							/>
							<div
								className='react-datepicker__custom-header__next'
								onClick={increaseMonth}
							/>
						</div>
					);
				}}
			/>
		</div>
	);
};

export default DatePicker;
